// ★ O PROTOCOLO DO AVATAR AO VIVO — os fatos MEDIDOS do provedor (LiveAvatar
// LITE / HeyGen), spec-009.
//
// Mesma disciplina de `transporte-tempo-real-protocolo.js`: aqui só entra o
// que foi observado numa sessão real, nunca o que uma doc genérica promete.
// Tudo abaixo foi medido em 18/09/2026, no SANDBOX (0 créditos), com o PCM
// que o nosso próprio TTS produziu — artefatos em `~/aawz-tmp/avatar-ao-vivo/`.
//
// ## O que a sessão faz, e o que ela NÃO faz
//
// O provedor não conversa: ele **renderiza vídeo a partir do áudio que nós
// mandamos**. Não há LLM, não há TTS e não há turno do lado dele — o
// raciocínio e a fala continuam inteiramente na AAWZ. É por isso que este
// caminho consegue falar o que o Modo de Voz não conseguiu: a fala sai do
// NOSSO TTS, e não de um modelo que pode recusá-la ([T071] mediu 26
// `commentary.append` enviados e 0 ecoados com a delegação aberta).
//
// ⚠️ **Este arquivo não é alcançado pelo Modo de Voz (GPT-Live).** São duas
// portas separadas por decisão do owner (18/09/2026). Nenhuma constante daqui
// é lida por `transporte-tempo-real*.js`, e vice-versa.

/** O canal de comando é um WebSocket por sessão — a URL vem do servidor. */
export const NOME_DO_CANAL = 'liveavatar-lite'

// ---------------------------------------------------------------------------
// O formato do áudio — MEDIDO nas duas pontas
// ---------------------------------------------------------------------------

/**
 * PCM 16-bit mono a 24 kHz é o ÚNICO formato que o `agent.speak` aceita.
 *
 * ★ Ele também é um formato que o ElevenLabs entrega DIRETO
 * (`output_format=pcm_24000` → `Content-Type: audio/pcm`), o que significa
 * que **nada é convertido no navegador**: o áudio sai do nosso TTS já no
 * formato do destino. Medido: 254.120 bytes para 5,29 s, que é exatamente
 * `24000 * 2` bytes por segundo — sem sobra, sem cabeçalho, PCM cru.
 *
 * Reamostrar MP3 aqui (decodificar com `AudioContext`, reamostrar num
 * `OfflineAudioContext`, requantizar) seria três lugares novos onde a
 * sincronia labial pode derivar. Pedir na origem elimina os três.
 */
export const TAXA_HZ = 24000
/**
 * O `Content-Type` com que o NOSSO servidor devolve esse PCM
 * (`usecase_voz_tts.CONTENT_TYPE_PCM`). Ele é o discriminante no momento de
 * TOCAR: o formato é escolhido quando a frase é SINTETIZADA, e o avatar pode
 * ser ligado entre a síntese e a fala — as frases da boas-vindas já estão em
 * MP3 quando isso acontece. Mandar MP3 como se fosse PCM faz o avatar falar
 * ruído, e o provedor responde `audio_buffer_appended` normalmente.
 */
export const CONTENT_TYPE_PCM = 'audio/pcm'

/** 16 bits = 2 bytes por amostra. Fatiar em número ÍMPAR de bytes parte uma
 *  amostra ao meio e o avatar fala estalando. */
export const BYTES_POR_AMOSTRA = 2

/**
 * ~1 segundo por chunk — o tamanho que o provedor RECOMENDA e que a medição
 * confirmou (6 chunks de 48.000 B para 5,29 s, todos com
 * `agent.audio_buffer_appended` e nenhum `video_starvation`).
 *
 * O teto do provedor é 1 MB por quadro de WebSocket; 48 KB fica uma ordem de
 * grandeza abaixo, então nem o chunk final irregular chega perto.
 */
export const BYTES_POR_CHUNK = TAXA_HZ * BYTES_POR_AMOSTRA

// ---------------------------------------------------------------------------
// Os eventos — só os que foram OBSERVADOS
// ---------------------------------------------------------------------------

/** O que MANDAMOS. */
export const COMANDO = Object.freeze({
  /** Um pedaço de áudio da fala atual. O `event_id` do PRIMEIRO chunk
   *  NOMEIA a utterance; os seguintes podem repetir o mesmo id. */
  FALAR: 'agent.speak',
  /** Sela a fala. Sem ele o avatar espera mais áudio e nunca termina. */
  FIM_DA_FALA: 'agent.speak_end',
  /** Barge-in: limpa o buffer e sela a fala em curso. É o que `interromper`
   *  usa — nunca fechar o socket, que mataria a sessão inteira. */
  INTERROMPER: 'agent.interrupt',
  /** Pose de escuta (do repouso). Cosmético: diz à pessoa que é a vez dela. */
  ESCUTANDO: 'agent.start_listening',
  /** Volta ao repouso. */
  PARAR_DE_ESCUTAR: 'agent.stop_listening',
  /** Heartbeat. Sem ele a sessão morre por ociosidade em 5 minutos. */
  MANTER_VIVA: 'session.keep_alive',
})

/** O que RECEBEMOS. Os seis primeiros foram observados na sonda. */
export const EVENTO = Object.freeze({
  ESTADO_DA_SESSAO: 'session.state_updated',
  ESTADO_DO_AVATAR: 'agent.state_updated',
  COMECOU_A_FALAR: 'agent.speak_started',
  TERMINOU_DE_FALAR: 'agent.speak_ended',
  FOI_INTERROMPIDO: 'agent.speak_interrupted',
  AUDIO_RECEBIDO: 'agent.audio_buffer_appended',
  AUDIO_SELADO: 'agent.audio_buffer_committed',
  AUDIO_LIMPO: 'agent.audio_buffer_cleared',
  ERRO: 'error',
  AVISO: 'warning',
})

/**
 * ⚠️ NENHUM comando é aceito antes de `session.state_updated` com este estado.
 * Medido: ele chega uma vez, sozinho, logo após o socket abrir.
 */
export const SESSAO_CONECTADA = 'connected'
export const SESSAO_DESCONECTADA = 'disconnected'

/** Estados do avatar (`agent.state_updated`). */
export const AVATAR = Object.freeze({
  PARADO: 'idle',
  ESCUTANDO: 'listening',
  FALANDO: 'talking',
})

// ---------------------------------------------------------------------------
// Prazos
// ---------------------------------------------------------------------------

/**
 * O heartbeat. O provedor derruba por 5 minutos de ociosidade; 60 s dá cinco
 * tentativas de margem antes disso — e uma conversa de primeiro contato tem
 * silêncios longos de propósito (a pessoa está pensando no que responder).
 */
export const MS_ENTRE_HEARTBEATS = 60000

/**
 * Teto para o `session.state_updated: connected` chegar. Medido: instantâneo
 * no sandbox. 15 s é margem para rede ruim — passado isso, é melhor cair no
 * retrato estático do que deixar a pessoa olhando para um quadrado preto.
 */
export const MS_ATE_CONECTAR = 15000

/**
 * Teto para `agent.speak_started` depois de `agent.speak_end`. Medido: 0,40 s.
 * 10 s é duas ordens de grandeza acima — quem estoura isto não está lento,
 * está quebrado, e a fila de fala precisa seguir em vez de travar para sempre.
 */
export const MS_ATE_COMECAR_A_FALAR = 10000

/** Motivos de queda, nomeados — viajam para a medição, nunca para a tela. */
export const MOTIVO = Object.freeze({
  SEM_WEBSOCKET: 'sem_websocket',
  SEM_SDK_DE_VIDEO: 'sem_sdk_de_video',
  NAO_CONECTOU: 'nao_conectou',
  CANAL_CAIU: 'canal_caiu',
  SESSAO_RECUSADA: 'sessao_recusada',
  SALA_RECUSADA: 'sala_recusada',
  ERRO_DO_PROVEDOR: 'erro_do_provedor',
})

/** O ambiente tem o que é preciso para abrir o canal? */
export const suportaWebSocket = () =>
  typeof WebSocket !== 'undefined' && typeof window !== 'undefined'
